// @ts-nocheck
/**
 * Pure helpers for viewing-mode table/chart paint from frozen RenderDescriptor props.
 * Shared by RN (optional) and web HtmlDescriptorAdapter — no host I/O / expressions.
 */

export interface FrozenTableRow {
  values: ReadonlyArray<string | number | null>;
}

function columnTypesFromTableProps(props: Record<string, unknown>): string[] {
  const schema = props.schema;
  if (!schema || typeof schema !== 'object') return [];
  const columns = (schema as { columns?: unknown }).columns;
  if (!Array.isArray(columns)) return [];
  return columns.map((col) => {
    if (!col || typeof col !== 'object') return '';
    return String((col as { type?: unknown }).type ?? '');
  });
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function looksLikeEpochMs(value: number): boolean {
  return value >= 1e12 && value < 1e14;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Compact axis tick: `dd.MM HH:mm`. */
export function formatChartTickDateTime(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(ms);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Table datetime cell — local wall clock, same idea as Containd spreadsheet display. */
export function formatFrozenDateTimeCell(value: unknown): string | null {
  const n = coerceFiniteNumber(value);
  if (n == null || !looksLikeEpochMs(n)) return null;
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return null;
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

export function formatFrozenTableCell(value: unknown, columnType?: string): string {
  if (value == null || value === '') return '';
  const type = (columnType ?? '').toLowerCase();
  if (type === 'timestamp' || type === 'datetime' || type === 'date') {
    const formatted = formatFrozenDateTimeCell(value);
    if (formatted) return formatted;
  }
  return String(value);
}

export function tablePreviewRowsFromProps(props: Record<string, unknown>): {
  headers: string[];
  rows: string[][];
  rowCount: number;
} {
  const headers = Array.isArray(props.columnHeaders)
    ? (props.columnHeaders as unknown[]).map((h) => String(h ?? ''))
    : [];
  const columnTypes = columnTypesFromTableProps(props);
  const stringifyRow = (row: unknown[]): string[] =>
    row.map((c, i) => formatFrozenTableCell(c, columnTypes[i]));
  if (Array.isArray(props.previewRows)) {
    const rows = (props.previewRows as unknown[]).map((r) =>
      Array.isArray(r) ? stringifyRow(r) : [],
    );
    const rowCount =
      typeof props.rowCount === 'number' ? props.rowCount : rows.length;
    return { headers, rows, rowCount };
  }
  const rawRows = Array.isArray(props.rows)
    ? (props.rows as FrozenTableRow[])
    : [];
  const rows = rawRows.map((r) => stringifyRow([...(r?.values ?? [])]));
  return { headers, rows, rowCount: rows.length };
}

export interface FrozenChartPoint {
  x: string | number | null;
  y: number;
  label?: string;
  color?: string;
}

export function chartSeriesFromProps(
  props: Record<string, unknown>,
): FrozenChartPoint[] {
  if (!Array.isArray(props.series)) return [];
  return (props.series as FrozenChartPoint[]).filter(
    (p) => p && typeof p.y === 'number' && Number.isFinite(p.y),
  );
}

export interface FrozenChartPlotPoint {
  x: number;
  y: number;
  color: string;
}

/** ViewBox units (0..100). Matches ~3px host markers on a typical layout chart. */
export const LINE_CHART_MARKER_RADIUS = 2;

/** Build a simple SVG path for a line chart in viewBox 0..100. */
export function buildLineChartPath(
  series: FrozenChartPoint[],
  width = 100,
  height = 100,
  pad = 8,
): {
  pathD: string;
  bars: Array<{ x: number; y: number; w: number; h: number; color: string }>;
  points: FrozenChartPlotPoint[];
} {
  const bars: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    color: string;
  }> = [];
  const points: FrozenChartPlotPoint[] = [];
  if (series.length === 0) return { pathD: '', bars, points };

  const ys = series.map((p) => p.y);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 0);
  const spanY = maxY - minY || 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const n = series.length;

  const plotted = series.map((p, i) => {
    const x = pad + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = pad + innerH - ((p.y - minY) / spanY) * innerH;
    return { x, y, color: p.color ?? '#0a7ea4' };
  });
  points.push(...plotted);

  const pathD = plotted
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const barW = Math.max(2, innerW / Math.max(n * 1.5, 1));
  for (const [i, p] of plotted.entries()) {
    const zeroY = pad + innerH - ((0 - minY) / spanY) * innerH;
    const top = Math.min(p.y, zeroY);
    const h = Math.abs(p.y - zeroY);
    bars.push({
      x: p.x - barW / 2,
      y: top,
      w: barW,
      h: Math.max(h, 1),
      color: p.color ?? '#0a7ea4',
    });
  }

  return { pathD, bars, points };
}

export interface FrozenPlotSeries {
  id?: string;
  label?: string;
  color?: string;
  strokeWidth?: number;
  yAxis?: 'primary' | 'secondary';
  points?: FrozenChartPoint[];
}

const FALLBACK_SERIES_COLORS = ['#0a7ea4', '#c45c26', '#2e7d4f', '#7b2d8e', '#b8860b'];

function finiteYPoints(points: FrozenChartPoint[] | undefined): FrozenChartPoint[] {
  if (!Array.isArray(points)) return [];
  return points.filter((p) => p && typeof p.y === 'number' && Number.isFinite(p.y));
}

/** Prefer named plotSeries; fall back to a single primary series from flat `series`. */
function seriesColorFromMeta(
  props: Record<string, unknown>,
  index: number,
): string | undefined {
  const meta = props.seriesMeta;
  if (!Array.isArray(meta)) return undefined;
  const entry = meta[index];
  if (!entry || typeof entry !== 'object') return undefined;
  const color = (entry as { color?: unknown }).color;
  if (typeof color !== 'string') return undefined;
  const trimmed = color.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function chartPlotSeriesFromProps(props: Record<string, unknown>): FrozenPlotSeries[] {
  const chartSeriesColor =
    typeof props.seriesColor === 'string' && props.seriesColor.trim().length > 0
      ? props.seriesColor.trim()
      : undefined;
  if (Array.isArray(props.plotSeries) && props.plotSeries.length > 0) {
    return (props.plotSeries as FrozenPlotSeries[]).map((s, index) => ({
      id: s.id ?? `s${index}`,
      label: s.label,
      color:
        s.color ||
        seriesColorFromMeta(props, index) ||
        chartSeriesColor ||
        FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length],
      strokeWidth: s.strokeWidth,
      yAxis: s.yAxis === 'secondary' ? 'secondary' : 'primary',
      points: finiteYPoints(s.points),
    }));
  }
  const flat = chartSeriesFromProps(props);
  if (flat.length === 0) return [];
  return [{ id: 'y', points: flat, yAxis: 'primary' }];
}

export interface ProjectedPlotSeries {
  id: string;
  color: string;
  strokeWidth: number;
  yAxis: 'primary' | 'secondary';
  pathD: string;
  bars: Array<{ x: number; y: number; w: number; h: number; color: string }>;
  points: FrozenChartPlotPoint[];
}

function yRangeFor(
  seriesList: FrozenPlotSeries[],
  axis: 'primary' | 'secondary',
): { min: number; max: number } {
  const ys = seriesList
    .filter((s) => (s.yAxis ?? 'primary') === axis)
    .flatMap((s) => (s.points ?? []).map((p) => p.y));
  if (ys.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...ys, 0);
  const max = Math.max(...ys, 0);
  return { min, max: max === min ? min + 1 : max };
}

function projectY(
  y: number,
  range: { min: number; max: number },
  height: number,
  pad: number,
): number {
  const innerH = height - pad * 2;
  const span = range.max - range.min || 1;
  return pad + innerH - ((y - range.min) / span) * innerH;
}

/**
 * Project all plotSeries into viewBox coordinates with independent primary/secondary Y scales.
 */
export function projectPlotSeriesToViewBox(
  seriesList: FrozenPlotSeries[],
  width = 100,
  height = 100,
  pad = 8,
): {
  projected: ProjectedPlotSeries[];
  hasSecondary: boolean;
} {
  const hasSecondary = seriesList.some((s) => s.yAxis === 'secondary');
  const rightPad = hasSecondary ? pad + 4 : pad;
  const primaryRange = yRangeFor(seriesList, 'primary');
  const secondaryRange = yRangeFor(seriesList, 'secondary');
  const innerW = width - pad - rightPad;
  const projected: ProjectedPlotSeries[] = [];

  for (const [index, series] of seriesList.entries()) {
    const pts = series.points ?? [];
    const n = pts.length;
    const axis = series.yAxis === 'secondary' ? 'secondary' : 'primary';
    const range = axis === 'secondary' ? secondaryRange : primaryRange;
    const color = series.color || FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length]!;
    const strokeWidth = typeof series.strokeWidth === 'number' ? series.strokeWidth : 2;
    const mapped = pts.map((p, i) => {
      const x = pad + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
      const y = projectY(p.y, range, height, pad);
      return { x, y, color: p.color ?? color };
    });
    const pathD = mapped
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');
    const barW = Math.max(2, innerW / Math.max(n * 1.5, 1));
    const zeroY = projectY(0, range, height, pad);
    const bars = mapped.map((p) => {
      const top = Math.min(p.y, zeroY);
      const h = Math.abs(p.y - zeroY);
      return { x: p.x - barW / 2, y: top, w: barW, h: Math.max(h, 1), color: p.color };
    });
    projected.push({
      id: series.id ?? `s${index}`,
      color,
      strokeWidth,
      yAxis: axis,
      pathD,
      bars,
      points: mapped,
    });
  }

  return { projected, hasSecondary };
}

export interface FrozenPieSlice {
  pathD: string;
  color: string;
  label?: string;
}

export function buildPieSlicesFromSeries(
  seriesList: FrozenPlotSeries[],
  cx = 50,
  cy = 50,
  radius = 36,
): FrozenPieSlice[] {
  const points = seriesList[0]?.points ?? [];
  const total = points.reduce((sum, p) => sum + Math.max(0, p.y), 0);
  if (total <= 0 || points.length === 0) return [];
  let angle = -Math.PI / 2;
  return points.map((p, index) => {
    const sweep = (Math.max(0, p.y) / total) * Math.PI * 2;
    const a0 = angle;
    const a1 = angle + sweep;
    angle = a1;
    const x0 = cx + radius * Math.cos(a0);
    const y0 = cy + radius * Math.sin(a0);
    const x1 = cx + radius * Math.cos(a1);
    const y1 = cy + radius * Math.sin(a1);
    const large = sweep > Math.PI ? 1 : 0;
    const color =
      p.color ||
      seriesList[0]?.color ||
      FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length]!;
    return {
      pathD: `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`,
      color,
      label: p.label,
    };
  });
}

export interface FrozenBoxPlotGeom {
  minY: number;
  q1Y: number;
  medianY: number;
  q3Y: number;
  maxY: number;
  x: number;
  boxWidth: number;
  color: string;
}

export function buildBoxPlotGeom(
  seriesList: FrozenPlotSeries[],
  width = 100,
  height = 100,
  pad = 8,
): FrozenBoxPlotGeom | null {
  const ys = (seriesList[0]?.points ?? []).map((p) => p.y).sort((a, b) => a - b);
  if (ys.length < 5) return null;
  const min = ys[0]!;
  const q1 = ys[Math.floor((ys.length - 1) * 0.25)]!;
  const median = ys[Math.floor((ys.length - 1) * 0.5)]!;
  const q3 = ys[Math.floor((ys.length - 1) * 0.75)]!;
  const max = ys[ys.length - 1]!;
  const range = { min: Math.min(min, 0), max: max === min ? min + 1 : max };
  const color = seriesList[0]?.color || FALLBACK_SERIES_COLORS[0]!;
  return {
    minY: projectY(min, range, height, pad),
    q1Y: projectY(q1, range, height, pad),
    medianY: projectY(median, range, height, pad),
    q3Y: projectY(q3, range, height, pad),
    maxY: projectY(max, range, height, pad),
    x: width / 2,
    boxWidth: 18,
    color,
  };
}

export interface ChartLegendItem {
  id: string;
  label: string;
  color: string;
}

/** Legend swatches from frozen plotSeries (id/label/color). */
export function chartLegendItemsFromPlotSeries(
  seriesList: FrozenPlotSeries[],
): ChartLegendItem[] {
  return seriesList.map((s, index) => ({
    id: s.id ?? `s${index}`,
    label: (typeof s.label === 'string' && s.label.trim().length > 0
      ? s.label.trim()
      : s.id) || `Series ${index + 1}`,
    color: s.color || FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length]!,
  }));
}

export interface ChartAxisTick {
  x?: number;
  y?: number;
  label: string;
}

function formatTickLabel(value: string | number | null | undefined): string {
  if (value == null) return '';
  const numeric = coerceFiniteNumber(value);
  if (numeric != null) {
    if (looksLikeEpochMs(numeric)) {
      return formatChartTickDateTime(numeric);
    }
    if (Number.isInteger(numeric) || Math.abs(numeric - Math.round(numeric)) < 1e-6) {
      return String(Math.round(numeric));
    }
    const abs = Math.abs(numeric);
    if (abs >= 1000 || abs < 0.01) return numeric.toPrecision(3);
    return String(Math.round(numeric * 100) / 100);
  }
  return String(value);
}

function sampleIndices(count: number, maxTicks: number): number[] {
  if (count <= 0) return [];
  const n = Math.min(maxTicks, count);
  if (n === 1) return [0];
  const out: number[] = [];
  for (let i = 0; i < n; i += 1) {
    out.push(Math.round((i * (count - 1)) / (n - 1)));
  }
  return [...new Set(out)];
}

/**
 * Tick marks in viewBox units aligned with projectPlotSeriesToViewBox.
 */
export function chartAxisTicksFromPlotSeries(
  seriesList: FrozenPlotSeries[],
  width = 100,
  height = 100,
  pad = 8,
): {
  xTicks: Array<{ x: number; label: string }>;
  yTicks: Array<{ y: number; label: string }>;
  ySecondaryTicks: Array<{ y: number; label: string }>;
} {
  const hasSecondary = seriesList.some((s) => s.yAxis === 'secondary');
  const rightPad = hasSecondary ? pad + 4 : pad;
  const innerW = width - pad - rightPad;

  const xSource =
    seriesList.find((s) => (s.points?.length ?? 0) > 0)?.points ?? [];
  const n = xSource.length;
  const xTicks = sampleIndices(n, 5).map((i) => {
    const p = xSource[i]!;
    const x = pad + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    return { x, label: formatTickLabel(p.label ?? p.x) };
  });

  const yTicksFor = (axis: 'primary' | 'secondary'): Array<{ y: number; label: string }> => {
    const inAxis = seriesList.filter((s) => (s.yAxis ?? 'primary') === axis);
    if (inAxis.every((s) => (s.points?.length ?? 0) === 0)) return [];
    const range = yRangeFor(seriesList, axis);
    return [0, 0.25, 0.5, 0.75, 1].map((t) => {
      const value = range.min + t * (range.max - range.min);
      return { y: projectY(value, range, height, pad), label: formatTickLabel(value) };
    });
  };

  return {
    xTicks,
    yTicks: yTicksFor('primary'),
    ySecondaryTicks: hasSecondary ? yTicksFor('secondary') : [],
  };
}
